import { forwardRef, useMemo } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { rehypeTables, rehypeTaskItems } from '../hast'
import type { MarkdownProps } from '../index'
import { CodeBlock } from './code-block'
import { Paragraph } from './paragraph'
import { Prose } from './prose'
import { Table } from './table'

type ReadOnlyProps = Pick<
  MarkdownProps,
  | 'size'
  | 'ladder'
  | 'measure'
  | 'lineNumbers'
  | 'className'
  | 'components'
  | 'remarkPlugins'
  | 'rehypePlugins'
  | 'urlTransform'
  | 'renderImage'
  | 'bare'
> & {
  source: string
}

const REMARK: NonNullable<ReadOnlyProps['remarkPlugins']> = [remarkGfm]
const REHYPE: NonNullable<ReadOnlyProps['rehypePlugins']> = [rehypeTables, rehypeTaskItems]

const ReadOnly = forwardRef<HTMLDivElement, ReadOnlyProps>(function ReadOnly(
  {
    source,
    size,
    ladder,
    measure,
    lineNumbers = false,
    className,
    components,
    remarkPlugins,
    rehypePlugins,
    urlTransform,
    renderImage,
    bare = false,
  },
  ref,
) {
  const map = useMemo<Components>(
    () => ({
      p: Paragraph,
      table: Table,
      pre: (props) => <CodeBlock {...props} lineNumbers={lineNumbers} />,
      ...(renderImage && {
        img: ({ node: _node, src, alt, title, className: imageClass }) =>
          renderImage({ src: typeof src === 'string' ? src : '', alt, title, className: imageClass }),
      }),
      ...components,
    }),
    [lineNumbers, renderImage, components],
  )
  const remark = useMemo(() => (remarkPlugins ? [...REMARK, ...remarkPlugins] : REMARK), [remarkPlugins])
  const rehype = useMemo(() => (rehypePlugins ? [...REHYPE, ...rehypePlugins] : REHYPE), [rehypePlugins])

  const content = (
    <ReactMarkdown remarkPlugins={remark} rehypePlugins={rehype} components={map} urlTransform={urlTransform}>
      {source}
    </ReactMarkdown>
  )

  if (bare) return content

  return (
    <Prose ref={ref} size={size} ladder={ladder} measure={measure} className={className}>
      {content}
    </Prose>
  )
})

ReadOnly.displayName = 'Markdown.ReadOnly'

export { ReadOnly }
export type { ReadOnlyProps }
